/**
 * QC workflow + tags API (global workflows, tag lists per workflow).
 * Uses the shared {@link ../../api/axiosQcClient.js} instance (Auth0 bearer).
 */
import { qcAxios } from '../../api/axiosQcClient';

const API_PREFIX = '/api';

function unwrapDataArray(body) {
  const rows = body?.data;
  return Array.isArray(rows) ? rows : [];
}

/**
 * `getAllTags` response: `data` is either
 * `[{ workflow_id, tags: string[] }, ...]`, or (legacy) `{ [workflow_id]: string[] }`.
 */
function tagsByWorkflowIdFromGetAllTagsBody(body) {
  const d = body?.data;
  /** @type {Record<string, string[]>} */
  const out = {};

  if (Array.isArray(d)) {
    for (const item of d) {
      if (!item || typeof item !== 'object') continue;
      const wfRaw = item.workflow_id ?? item.workflowId;
      if (wfRaw == null || String(wfRaw).trim() === '') continue;
      const key = String(wfRaw).trim();
      const tags = item.tags;
      out[key] = Array.isArray(tags) ? tags.map((x) => String(x)) : [];
    }
    return out;
  }

  if (d != null && typeof d === 'object' && !Array.isArray(d)) {
    for (const key of Object.keys(d)) {
      const v = /** @type {Record<string, unknown>} */ (d)[key];
      out[key] = Array.isArray(v) ? v.map((x) => String(x)) : [];
    }
    return out;
  }

  return out;
}

function normalizeTagRow(row) {
  if (!row || typeof row !== 'object') return null;
  const tag_id = row.tag_id ?? row.tagId;
  if (tag_id === undefined || tag_id === null) return null;
  const tag_name = row.tag_name ?? row.tagName;
  const wf = row.workflow_id ?? row.workflowId;
  return {
    tag_id,
    tag_name: tag_name != null ? String(tag_name) : '',
    workflow_id: wf != null && wf !== '' ? String(wf) : '',
  };
}

function tagNameStringToRow(tagName, workflowId, index) {
  return {
    tag_id: `${workflowId}:${index}`,
    tag_name: tagName,
    workflow_id: workflowId,
  };
}

export async function getGlobalWorkflows() {
  const { data: body } = await qcAxios.get(
    `${API_PREFIX}/wf/getGlobalWorkflows`,
  );
  return unwrapDataArray(body);
}

export async function getAllTags() {
  const { data: body } = await qcAxios.get(`${API_PREFIX}/wf/getAllTags`);
  return tagsByWorkflowIdFromGetAllTagsBody(body);
}

export async function getAllTagsForWorkflow(workflowId) {
  if (!workflowId) return [];
  const { data: body } = await qcAxios.get(
    `${API_PREFIX}/wf/getAllTagsForWorkflow`,
    {
      params: { workflowId },
    },
  );
  return unwrapDataArray(body)
    .map((row) => normalizeTagRow(row))
    .filter(Boolean);
}

/**
 * @returns {Promise<{
 *   tagsByWorkflowId: Record<string, Array<{ tag_id: string, tag_name: string, workflow_id: string }>>,
 *   workflowIds: string[],
 *   globalWorkflows: Array<Record<string, unknown>>,
 * }>}
 */
export async function fetchAllWorkflowTags() {
  const globalWorkflows = await getGlobalWorkflows();
  const workflowIds = globalWorkflows
    .map((w) => w?.workflow_id ?? w?.workflowId)
    .filter((id) => id !== undefined && id !== null && String(id).trim() !== '')
    .map((id) => String(id));

  if (workflowIds.length === 0) {
    return {
      tagsByWorkflowId: {},
      workflowIds: [],
      globalWorkflows: Array.isArray(globalWorkflows) ? globalWorkflows : [],
    };
  }

  let tagsMap = {};
  try {
    tagsMap = await getAllTags();
  } catch {
    tagsMap = {};
  }

  /** @type {Record<string, Array<{ tag_id: string, tag_name: string, workflow_id: string }>>} */
  const tagsByWorkflowId = {};

  for (const wfId of workflowIds) {
    const names = tagsMap[wfId];
    const list = Array.isArray(names) ? names : [];
    tagsByWorkflowId[wfId] = list.map((name, index) =>
      tagNameStringToRow(String(name), wfId, index),
    );
  }

  return {
    tagsByWorkflowId,
    workflowIds,
    globalWorkflows: Array.isArray(globalWorkflows) ? globalWorkflows : [],
  };
}
